import { inject, injectable } from 'inversify'
import { makeObservable, observable, runInAction } from 'mobx'
import { HttpGateway } from '@core/services/http.gateway'
import { IdGeneratorGateway } from '@core/services/id-generator.gateway'
import { Types } from '@core/ioc/types'
import type { UserScopedState } from '@core/interfaces/user-scoped-state'
import { MessagePacking, type ActionFailurePm, type ActionResponsePm } from '@core/feedback/feedback.packing'
import { mapReadFailure } from '@core/services/gateway-failure.helpers'
import { z } from '@core/validation/validation.schema'
import { OperationRepository } from '@core/operations/operation.repository'
import type {
  OperationHandle,
  OperationOwner,
  OperationResult,
  ReadonlyOperationState,
} from '@core/operations/operation.types'
import {
  defineOperation,
  operationFailed,
  operationGroup,
  operationId,
  operationKey,
  operationSucceeded,
} from '@core/operations/operation.helpers'

type LoadProfileFailure = ActionFailurePm & Readonly<{ retryable: boolean }>

export type ProfilePm = {
  readonly email: string
  // null = the user has never set a display name. Every screen falls back to
  // the email (see person-identity.helpers.ts).
  readonly displayName: string | null
}

const ProfileDtoSchema = z.object({
  email: z.string(),
  displayName: z.string().nullable(),
})

const LOAD_PROFILE_OPERATION = defineOperation<null, LoadProfileFailure>({
  id: operationId('profile.load'),
  key: () => operationKey('profile'),
  groups: () => [operationGroup('session'), operationGroup('profile')],
  pending: { kind: 'join' },
  safety: { kind: 'read' },
  timeout: {
    kind: 'after',
    milliseconds: 15_000,
    toFailure: () => ({ code: 'temporarily-unavailable', retryable: false }),
  },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

type SaveArgs = Readonly<{ displayName: string | null; idempotencyKey: string }>

const SAVE_STATE_SENTINEL: SaveArgs = Object.freeze({ displayName: null, idempotencyKey: '' })

type ChangePasswordArgs = Readonly<{ idempotencyKey: string }>
const CHANGE_PASSWORD_SENTINEL: ChangePasswordArgs = Object.freeze({ idempotencyKey: '' })

const CHANGE_PASSWORD_OPERATION = defineOperation<ChangePasswordArgs, ActionFailurePm>({
  id: operationId('profile.change-password'),
  key: () => operationKey('command'),
  groups: () => [operationGroup('session'), operationGroup('profile')],
  pending: { kind: 'ignore' },
  safety: { kind: 'idempotency-keyed-write', idempotencyKey: (args) => args.idempotencyKey },
  timeout: { kind: 'after', milliseconds: 10_000, toFailure: () => ({ code: 'temporarily-unavailable' }) },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

const SAVE_PROFILE_OPERATION = defineOperation<SaveArgs, ActionFailurePm>({
  id: operationId('profile.save'),
  key: () => operationKey('command'),
  groups: () => [operationGroup('session'), operationGroup('profile')],
  pending: { kind: 'ignore' },
  safety: { kind: 'idempotency-keyed-write', idempotencyKey: (args) => args.idempotencyKey },
  timeout: { kind: 'after', milliseconds: 10_000, toFailure: () => ({ code: 'temporarily-unavailable' }) },
  retry: {
    kind: 'bounded',
    maxAttempts: 3,
    delayMilliseconds: (attempt) => 200 * 2 ** (attempt - 1),
    when: (failure) => failure.code === 'temporarily-unavailable' || failure.code === 'offline',
  },
  debounce: { kind: 'none' },
})

/**
 * The signed-in user's own profile — currently just a display name. Lives in
 * `core/infra/authentication/` rather than under a feature because the
 * account itself is a liftable capability, not a domain concern. A feature
 * that shows or edits it (a Settings page) composes this rather than owning
 * its own copy.
 *
 * User-scoped (P-28): the profile is *this* viewer's, forgotten at logout.
 */
@injectable()
export class ProfileRepository implements UserScopedState {
  profilePm: ProfilePm | null = null

  private readonly dataGateway: HttpGateway
  private readonly idGenerator: IdGeneratorGateway
  private readonly operationOwner: OperationOwner
  private readonly loadOperation: OperationHandle<null, LoadProfileFailure>
  private readonly saveOperation: OperationHandle<SaveArgs, ActionFailurePm>
  private readonly changePasswordOperation: OperationHandle<ChangePasswordArgs, ActionFailurePm>

  constructor(
    @inject(Types.IDataGateway) dataGateway: HttpGateway,
    @inject(OperationRepository) operationRepository: OperationRepository,
    @inject(IdGeneratorGateway) idGenerator: IdGeneratorGateway,
  ) {
    this.dataGateway = dataGateway
    this.idGenerator = idGenerator
    this.operationOwner = operationRepository.createOwner({ id: 'profile.repository', lifetime: 'session' })
    this.loadOperation = this.operationOwner.register(LOAD_PROFILE_OPERATION)
    this.saveOperation = this.operationOwner.register(SAVE_PROFILE_OPERATION)
    this.changePasswordOperation = this.operationOwner.register(CHANGE_PASSWORD_OPERATION)

    makeObservable(this, { profilePm: observable.ref })
  }

  get changePasswordState(): ReadonlyOperationState<ActionFailurePm> {
    return this.changePasswordOperation.stateFor(CHANGE_PASSWORD_SENTINEL)
  }

  get isLoaded(): boolean {
    return this.profilePm !== null
  }

  get loadState(): ReadonlyOperationState<LoadProfileFailure> {
    return this.loadOperation.stateFor(null)
  }

  get saveState(): ReadonlyOperationState<ActionFailurePm> {
    return this.saveOperation.stateFor(SAVE_STATE_SENTINEL)
  }

  load = async (): Promise<ActionResponsePm> => {
    const result = await this.loadOperation.run(
      null,
      async ({ signal }) => {
        const response = await this.dataGateway.get('/me/profile', { signal, idempotencyKey: null })
        if (!response.success) return operationFailed(mapReadFailure(response))

        const parsed = ProfileDtoSchema.safeParse(response.result)
        if (!parsed.success) return operationFailed({ code: 'invalid-external-data', retryable: false })

        return operationSucceeded(Object.freeze({ email: parsed.data.email, displayName: parsed.data.displayName }))
      },
      {
        succeeded: (profile) => {
          this.profilePm = profile
        },
        failed: null,
      },
    )
    return this.toActionResponsePm(result)
  }

  save = async (displayName: string | null): Promise<ActionResponsePm> => {
    const normalised = displayName !== null && displayName.trim() !== '' ? displayName.trim() : null
    const result = await this.saveOperation.run(
      { displayName: normalised, idempotencyKey: this.idGenerator.generate() },
      async ({ signal, idempotencyKey }) => {
        const response = await this.dataGateway.post(
          '/me/profile',
          { displayName: normalised },
          { signal, idempotencyKey },
        )
        if (!response.success) {
          const pm = MessagePacking.unpackGatewayResponseToPm(response)
          return operationFailed(pm.success ? { code: 'temporarily-unavailable' } : pm.failure)
        }

        const parsed = ProfileDtoSchema.safeParse(response.result)
        if (!parsed.success) return operationFailed({ code: 'invalid-external-data' })

        return operationSucceeded(Object.freeze({ email: parsed.data.email, displayName: parsed.data.displayName }))
      },
      {
        succeeded: (saved) => {
          this.profilePm = saved
        },
        failed: null,
      },
    )
    return this.toActionResponsePm(result)
  }

  changePassword = async (currentPassword: string, newPassword: string): Promise<ActionResponsePm> => {
    const result = await this.changePasswordOperation.run(
      { idempotencyKey: this.idGenerator.generate() },
      async ({ signal, idempotencyKey }) => {
        const response = await this.dataGateway.post(
          '/me/password',
          { currentPassword, newPassword },
          { signal, idempotencyKey },
        )
        if (!response.success) {
          const pm = MessagePacking.unpackGatewayResponseToPm(response)
          return operationFailed(pm.success ? { code: 'temporarily-unavailable' } : pm.failure)
        }
        return operationSucceeded(null)
      },
      { succeeded: null, failed: null },
    )
    return this.toActionResponsePm(result)
  }

  resetUserScopedState = (): void => {
    this.operationOwner.resetAll({ kind: 'session-reset' })
    runInAction(() => {
      this.profilePm = null
    })
  }

  private toActionResponsePm(result: OperationResult<unknown, ActionFailurePm>): ActionResponsePm {
    switch (result.kind) {
      case 'succeeded':
        return MessagePacking.succeeded()
      case 'failed':
        return { success: false, failure: result.error }
      case 'cancelled':
      case 'skipped':
        return MessagePacking.cancelled()
    }
  }
}
